/**
 * Human-in-the-loop ingestion proposal review (Phase 11 frontend).
 *
 * Surfaced from the notification flow Phase 9 wired: given an ingestion run id,
 * it renders the AI change summary and, per proposed change, a current-vs-
 * proposed section diff ({@link DiffView}) with per-change accept / reject /
 * adjust controls, plus a batch "Confirm all". Nothing commits until the
 * provider acts (proposed-only; ADR-005). Proposed clinical content is shown but
 * never logged (HIPAA).
 *
 * Two layouts share this one component (no duplicated adjudication logic):
 * - `layout="inline"` (default): every change is expanded, for the chat-inline
 *   single-change `origin="chat"` case.
 * - `layout="panel"`: a collapsed one-line list with expand-on-demand + a sticky
 *   "Accept all remaining" / "Confirm all" footer, for the full-size transcript
 *   review panel that must scale to ~30 changes.
 *
 * ### Adjust flow (C-2 + B)
 * When the provider clicks "Adjust" the component enters **edit mode** for that
 * change: the `FrontmatterEditor` is shown (pre-populated from the proposed
 * `frontmatter` section), and a "Confirm adjustment" button replaces the normal
 * controls. On confirm:
 * 1. The proposed `frontmatter` section's JSON string is parsed back to a dict.
 * 2. The provider's edited salience / status / name are overlaid onto it.
 * 3. The `body` section's `s.new` string is read as-is.
 * 4. The payload `{ frontmatter: <full dict with overrides>, body: <string> }`
 *    is passed to `adjudicate(changeId, "edited", adjusted)`.
 * This keeps the required `id`, `patient_id`, `kind`, `name` keys intact so the
 * backend `parse_frontmatter` can validate the commit without throwing
 * `EntityValidationError`.
 *
 * Names over ids (#3/#8): a change shows its `entity_name` (fallback `target`)
 * and the run shows its `source_title` (via {@link sourceDisplayLabel}, which
 * suppresses a backend-scheme `source_ref` such as `derivative:<id>` rather than
 * leaking it); the raw id is kept only as a tooltip/`data-` attribute.
 */
import React, { useMemo, useState } from "react";
import { useIngestionReview } from "../../hooks/useIngestionReview";
import DiffView from "./DiffView";
import FrontmatterEditor from "./FrontmatterEditor";
import Markdown from "../blueprint/Markdown";
import { humanizeChangeType, sourceDisplayLabel } from "./proposalLabels";
import { humanizeStatus } from "../../utils/statusLabels";
import { isAnchorKind } from "../blueprint/cardRanking";
import type { IngestionChange, IngestionChangeStatus } from "../../types";
import type {
  EntityFrontmatter,
  SyncBackPayload,
  SyncBackResolution,
} from "../../types";
import { useRevealCard } from "../RevealCardContext";

/** A conflict resolution the provider can pick at the confirmation box. */
type ConflictChoice = "keep" | "replace" | "blend";

/** The conflict variant of a sync-back payload (ADR-011). */
type ConflictPayload = Extract<SyncBackPayload, { op: "conflict" }>;

/** The conflict payload of a change, or null when it is not a conflict. */
function conflictPayload(change: IngestionChange): ConflictPayload | null {
  return change.payload?.op === "conflict" ? change.payload : null;
}

/** Tailwind accent per per-change status. */
const STATUS_BADGE: Record<IngestionChangeStatus, string> = {
  pending: "bg-slate-100 text-slate-600",
  confirmed: "bg-emerald-100 text-emerald-700",
  edited: "bg-indigo-100 text-indigo-700",
  rejected: "bg-rose-100 text-rose-700",
};

/** How the review renders: chat-inline (always-expanded) vs full-panel (list). */
export type ProposalLayout = "inline" | "panel";

/** Props for {@link ProposalDiffReview}. */
interface ProposalDiffReviewProps {
  /** The ingestion run to review. */
  runId: string;
  /** Called once the run is fully resolved (e.g. to refresh the badge). */
  onResolved?: () => void;
  /**
   * Render mode. `"inline"` (default) expands every change for the chat-inline
   * single-change case; `"panel"` is a collapsed expand-on-demand list for the
   * full-size transcript panel (scales to ~30 changes).
   */
  layout?: ProposalLayout;
}

/** The display name of a change's target card (fallback to the raw id). */
function changeName(change: IngestionChange): string {
  const name = change.entity_name;
  return name !== null && name !== undefined && name !== ""
    ? name
    : change.target;
}

/** One read-only "not matched to a card" note (provider content with no card). */
interface UnmappedNote {
  /** The provider-edited passage that carries the unmatched clinical content. */
  quote: string;
  /** Why it maps to no provenance card (may be empty). */
  rationale: string;
}

/**
 * Normalize a raw `summary.unmapped` array into renderable notes.
 *
 * The backend (`_build_sync_back_summary`) serializes each unmapped item as an
 * object `{ evidence_quote, rationale }`; a bare string is tolerated defensively.
 * Items without any quote text are dropped so nothing renders empty.
 */
function normalizeUnmapped(raw: unknown): UnmappedNote[] {
  if (!Array.isArray(raw)) return [];
  const notes: UnmappedNote[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const quote = item.trim();
      if (quote) notes.push({ quote, rationale: "" });
      continue;
    }
    if (item !== null && typeof item === "object") {
      const obj = item as Record<string, unknown>;
      const quote =
        typeof obj.evidence_quote === "string" ? obj.evidence_quote.trim() : "";
      const rationale =
        typeof obj.rationale === "string" ? obj.rationale.trim() : "";
      if (quote) notes.push({ quote, rationale });
    }
  }
  return notes;
}

/**
 * Parse the proposed `frontmatter` section value to an EntityFrontmatter dict.
 *
 * The backend serialises the frontmatter section as a JSON string. We parse it
 * here so we can overlay provider edits before submission. Returns `null` if the
 * section is absent or not valid JSON (caller skips the editor in that case).
 */
function parseFrontmatter(change: IngestionChange): EntityFrontmatter | null {
  const fmSection = change.diff.sections.find(
    (s) => s.section === "frontmatter",
  );
  if (!fmSection) return null;
  const raw = fmSection.new;
  if (typeof raw !== "string" || raw.trim() === "") return null;
  try {
    return JSON.parse(raw) as EntityFrontmatter;
  } catch {
    return null;
  }
}

/**
 * Build the corrected `adjusted` payload for an "edited" adjudication.
 *
 * The backend `read_content` expects `{ frontmatter: dict, body: string }` —
 * NOT a flat `{ section: value }` map where frontmatter is a JSON *string*.
 *
 * Steps:
 * 1. Parse the proposed frontmatter JSON string.
 * 2. Overlay `editedFm` (provider's adjustments) onto it.
 * 3. Read the body section's `s.new` as the body string.
 *
 * When no frontmatter section is present the payload falls back to the flat
 * section map (legacy / non-entity changes) for backwards compatibility.
 */
function buildAdjustedPayload(
  change: IngestionChange,
  editedFm: EntityFrontmatter | null,
): Record<string, unknown> {
  const baseFm = parseFrontmatter(change);

  if (baseFm !== null && editedFm !== null) {
    const bodySection = change.diff.sections.find((s) => s.section === "body");
    const body = bodySection ? (bodySection.new ?? "") : "";
    const mergedFm: EntityFrontmatter = { ...baseFm, ...editedFm };
    return { frontmatter: mergedFm, body };
  }

  // Fallback: flat section map (non-entity changes, or no frontmatter section).
  return change.diff.sections.reduce<Record<string, string>>((acc, s) => {
    acc[s.section] = s.new;
    return acc;
  }, {});
}

/** Props for the shared adjust-edit-mode area. */
interface AdjustEditorProps {
  change: IngestionChange;
  onConfirm: (editedFm: EntityFrontmatter | null) => void;
  onCancel: () => void;
}

/**
 * The edit-mode area shown when a provider clicks "Adjust".
 *
 * Renders the {@link FrontmatterEditor} (when the change has a frontmatter
 * section) and the "Confirm adjustment" / "Cancel" buttons.
 */
function AdjustEditor({
  change,
  onConfirm,
  onCancel,
}: AdjustEditorProps): React.ReactElement {
  const initialFm = useMemo(() => parseFrontmatter(change), [change]);
  const [editedFm, setEditedFm] = useState<EntityFrontmatter | null>(initialFm);
  // Anchor cards have an immutable kind (enforced server-side); hide the Type
  // field so an adjudication adjust can't propose a re-type that would 422.
  const isAnchor = initialFm !== null && isAnchorKind(initialFm.kind);
  // Block confirm when a required field is blank (mirrors CardView's canSave).
  const canConfirm =
    editedFm === null ||
    (editedFm.name.trim() !== "" && editedFm.kind.trim() !== "");

  return (
    <div
      className="space-y-3 border-t border-slate-100 pt-3 mt-3"
      data-testid="adjust-editor"
    >
      {editedFm !== null && (
        <FrontmatterEditor
          value={editedFm}
          onChange={setEditedFm}
          isAnchor={isAnchor}
        />
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onConfirm(editedFm)}
          disabled={!canConfirm}
          data-testid="adjust-confirm"
          className="px-3 py-1 rounded bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-40"
        >
          Confirm adjustment
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 rounded border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The per-change accept / adjust / reject controls (shared by both layouts). */
function ChangeActions({
  onAccept,
  onAdjust,
  onReject,
}: {
  onAccept: () => void;
  onAdjust: () => void;
  onReject: () => void;
}): React.ReactElement {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onAccept}
        data-testid="change-accept"
        className="px-3 py-1 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
      >
        Accept
      </button>
      <button
        type="button"
        onClick={onAdjust}
        data-testid="change-adjust"
        className="px-3 py-1 rounded border border-indigo-300 text-indigo-700 text-xs font-medium hover:bg-indigo-50"
      >
        Adjust
      </button>
      <button
        type="button"
        onClick={onReject}
        data-testid="change-reject"
        className="px-3 py-1 rounded border border-rose-300 text-rose-700 text-xs font-medium hover:bg-rose-50"
      >
        Reject
      </button>
    </div>
  );
}

/** Whether a change is a sync-back conflict needing side-by-side resolution. */
function isConflict(change: IngestionChange): boolean {
  return change.payload?.op === "conflict";
}

/** One selectable side of the conflict resolver (a version to keep/apply). */
function ConflictOption({
  accent,
  eyebrow,
  line,
  label,
  selected,
  onSelect,
  testid,
}: {
  accent: string;
  eyebrow: string;
  line: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
  testid: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      aria-pressed={selected}
      className={`block w-full text-left rounded-lg border p-3 ${accent} ${
        selected ? "ring-2 ring-offset-1" : ""
      }`}
    >
      <div className="text-[11px] uppercase tracking-wide font-semibold mb-1">
        {eyebrow}
      </div>
      <div className="text-sm text-slate-700">
        <Markdown source={line} />
      </div>
      <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <span aria-hidden="true">{selected ? "●" : "○"}</span> {label}
      </span>
    </button>
  );
}

/**
 * The side-by-side conflict resolver (ADR-011): the current card line vs the
 * synced line, plus a blended transition line. The provider must pick one — no
 * bulk accept — mapping to keep→reject / replace / blend. The LLM never picks.
 */
function ConflictResolver({
  change,
  payload,
  onAdjudicate,
}: {
  change: IngestionChange;
  payload: ConflictPayload;
  onAdjudicate: (
    change: IngestionChange,
    decision: IngestionChangeStatus,
    editedFm: EntityFrontmatter | null,
    resolution?: SyncBackResolution,
  ) => void;
}): React.ReactElement {
  const [pick, setPick] = useState<ConflictChoice | null>(null);

  const resolve = (): void => {
    if (pick === "keep") onAdjudicate(change, "rejected", null);
    else if (pick === "replace")
      onAdjudicate(change, "confirmed", null, "replace");
    else if (pick === "blend") onAdjudicate(change, "confirmed", null, "blend");
  };

  return (
    <div className="space-y-3" data-testid="conflict-resolver">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ConflictOption
          testid="conflict-keep"
          accent="border-rose-200 bg-rose-50/50 ring-rose-400"
          eyebrow="Current — in the card now"
          line={payload.old_line}
          label="Keep current"
          selected={pick === "keep"}
          onSelect={() => setPick("keep")}
        />
        <ConflictOption
          testid="conflict-replace"
          accent="border-emerald-200 bg-emerald-50/50 ring-emerald-500"
          eyebrow="Synced — from this guide"
          line={payload.replace_line}
          label="Replace with synced"
          selected={pick === "replace"}
          onSelect={() => setPick("replace")}
        />
      </div>
      <ConflictOption
        testid="conflict-blend"
        accent="border-indigo-200 bg-indigo-50/50 ring-indigo-500"
        eyebrow="Blend — capture the transition"
        line={payload.blend_line}
        label="Blend into one transition line"
        selected={pick === "blend"}
        onSelect={() => setPick("blend")}
      />
      <button
        type="button"
        onClick={resolve}
        disabled={pick === null}
        data-testid="conflict-resolve"
        className="px-3 py-1 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Resolve
      </button>
    </div>
  );
}

/** One always-expanded change with its diff + controls (the inline layout). */
function ChangeCard({
  change,
  onAdjudicate,
  sourceTitles,
}: {
  change: IngestionChange;
  onAdjudicate: (
    change: IngestionChange,
    decision: IngestionChangeStatus,
    editedFm: EntityFrontmatter | null,
    resolution?: SyncBackResolution,
  ) => void;
  sourceTitles?: Record<string, string>;
}): React.ReactElement {
  const revealCard = useRevealCard();
  const [adjusting, setAdjusting] = useState(false);
  const pending = change.status === "pending";
  const conflict = conflictPayload(change);

  const handleAdjustConfirm = (editedFm: EntityFrontmatter | null): void => {
    setAdjusting(false);
    onAdjudicate(change, "edited", editedFm);
  };

  return (
    <div
      className="rounded-xl border border-slate-200 p-4 space-y-3"
      data-testid={`change-${change.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            {change.summary_line}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <div
              className="text-[11px] text-slate-500"
              title={change.target}
              data-card-target={change.target}
            >
              {humanizeChangeType(change.change_type)} → {changeName(change)}
            </div>
            {change.change_type !== "entity_created" && (
              <button
                type="button"
                aria-label="View card details"
                data-testid="reveal-in-blueprint"
                onClick={() => revealCard(change.target)}
                className="text-[11px] text-blue-500 hover:text-blue-700 font-medium"
              >
                View card details
              </button>
            )}
          </div>
        </div>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[change.status]}`}
        >
          {humanizeStatus(change.status)}
        </span>
      </div>

      {conflict && pending ? (
        <ConflictResolver
          change={change}
          payload={conflict}
          onAdjudicate={onAdjudicate}
        />
      ) : (
        <>
          <DiffView diff={change.diff} sourceTitles={sourceTitles} />
          {pending && !adjusting && (
            <ChangeActions
              onAccept={() => onAdjudicate(change, "confirmed", null)}
              onAdjust={() => setAdjusting(true)}
              onReject={() => onAdjudicate(change, "rejected", null)}
            />
          )}
          {pending && adjusting && (
            <AdjustEditor
              change={change}
              onConfirm={handleAdjustConfirm}
              onCancel={() => setAdjusting(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * One collapsed list row with expand-on-demand (the full-panel layout).
 *
 * A single-line summary (`{entity_name} · {kind/change_type}`) with inline
 * Accept/Reject; clicking the row toggles the diff + the full Adjust control.
 * This is what lets the panel scale cleanly to ~30 changes.
 */
function ChangeRow({
  change,
  onAdjudicate,
  sourceTitles,
}: {
  change: IngestionChange;
  onAdjudicate: (
    change: IngestionChange,
    decision: IngestionChangeStatus,
    editedFm: EntityFrontmatter | null,
    resolution?: SyncBackResolution,
  ) => void;
  sourceTitles?: Record<string, string>;
}): React.ReactElement {
  const revealCard = useRevealCard();
  const [expanded, setExpanded] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const pending = change.status === "pending";
  const conflict = conflictPayload(change);

  const handleAdjustConfirm = (editedFm: EntityFrontmatter | null): void => {
    setAdjusting(false);
    onAdjudicate(change, "edited", editedFm);
  };

  return (
    <div
      className="rounded-lg border border-slate-200"
      data-testid={`change-row-${change.id}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          aria-label={expanded ? "Collapse change" : "Expand change"}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          <span aria-hidden="true" className="text-slate-400 text-xs shrink-0">
            {expanded ? "▾" : "▸"}
          </span>
          <span
            className="truncate text-sm font-medium text-slate-800"
            title={change.target}
          >
            {changeName(change)}
          </span>
          <span className="text-[11px] text-slate-400 shrink-0">
            · {humanizeChangeType(change.change_type)}
          </span>
        </button>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${STATUS_BADGE[change.status]}`}
        >
          {humanizeStatus(change.status)}
        </span>
        {pending && isConflict(change) && (
          <span
            data-testid="conflict-badge"
            className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 bg-amber-100 text-amber-700 border border-amber-200"
          >
            ⚠ Resolve
          </span>
        )}
        {pending && !isConflict(change) && (
          <div className="flex gap-1 shrink-0">
            <button
              type="button"
              onClick={() => onAdjudicate(change, "confirmed", null)}
              className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[11px] font-medium hover:bg-emerald-700"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={() => onAdjudicate(change, "rejected", null)}
              className="px-2 py-0.5 rounded border border-rose-300 text-rose-700 text-[11px] font-medium hover:bg-rose-50"
            >
              Reject
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-3 py-3 space-y-3">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-700">
              {change.summary_line}
            </div>
            {change.change_type !== "entity_created" && (
              <button
                type="button"
                aria-label="View card details"
                data-testid="reveal-in-blueprint"
                onClick={() => revealCard(change.target)}
                className="text-[11px] text-blue-500 hover:text-blue-700 font-medium"
              >
                View card details
              </button>
            )}
          </div>
          {conflict && pending ? (
            <ConflictResolver
              change={change}
              payload={conflict}
              onAdjudicate={onAdjudicate}
            />
          ) : (
            <>
              <DiffView diff={change.diff} sourceTitles={sourceTitles} />
              {pending && !adjusting && (
                <ChangeActions
                  onAccept={() => onAdjudicate(change, "confirmed", null)}
                  onAdjust={() => setAdjusting(true)}
                  onReject={() => onAdjudicate(change, "rejected", null)}
                />
              )}
              {pending && adjusting && (
                <AdjustEditor
                  change={change}
                  onConfirm={handleAdjustConfirm}
                  onCancel={() => setAdjusting(false)}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Render the ingestion change-set review for one run.
 *
 * @param props - The run id, optional resolved callback, and layout.
 * @returns The proposal-review element.
 */
export const ProposalDiffReview: React.FC<ProposalDiffReviewProps> = ({
  runId,
  onResolved,
  layout = "inline",
}) => {
  const review = useIngestionReview(runId);

  if (review.status === "loading") {
    return <p className="text-sm text-slate-500">Loading review…</p>;
  }
  if (review.status === "error" || review.detail === null) {
    return <p className="text-sm text-rose-600">Unable to load this review.</p>;
  }

  const { run, changes } = review.detail;
  const anyPending = changes.some((c) => c.status === "pending");
  const sourceLabel = sourceDisplayLabel(run);
  // A run's cited spans are almost always spans of its own source (one
  // transcript per ingestion run); resolve them to the run's own resolved
  // title so diff citations read as "Source: Visit 1 Transcript" instead of
  // the neutral fallback. Absent when the title itself is unresolved — the
  // citation still degrades cleanly (never the raw id), just without a name.
  const sourceTitles =
    run.source_title !== null &&
    run.source_title !== undefined &&
    run.source_title.trim() !== ""
      ? { [run.source_ref]: run.source_title }
      : undefined;
  const unmappedNotes = normalizeUnmapped(run.summary.unmapped);
  // A changeless run (zero proposed changes) is an unmapped-only sync-back: there
  // is nothing to confirm or reject, so it gets an explicit Acknowledge control
  // instead of the (disabled) confirm footer — otherwise it would linger forever
  // in the pending-review bell with no UI path to clear it.
  const isChangeless = changes.length === 0;

  const adjudicate = (
    change: IngestionChange,
    decision: IngestionChangeStatus,
    editedFm: EntityFrontmatter | null,
    resolution?: SyncBackResolution,
  ): void => {
    const adjusted =
      decision === "edited"
        ? buildAdjustedPayload(change, editedFm)
        : undefined;
    // An individual pick that leaves the run terminal (no change still pending)
    // resolves the run — fire onResolved so the pending-review bell refreshes.
    // Bulk "Confirm all" skips conflicts, so this is the only path that clears a
    // conflict-containing run (ADR-011). The hook decides terminal from the
    // reloaded run state; a still-pending run resolves false and no-ops here.
    void review
      .adjudicate(
        change.id,
        decision,
        adjusted as Record<string, string> | undefined,
        resolution,
      )
      .then((resolved) => {
        if (resolved) onResolved?.();
      });
  };

  // "Accept all remaining" only closes the panel when the run is FULLY resolved.
  // If conflicts (or a stale change the backend skipped) remain pending, confirmAll
  // resolves false and the panel stays open with the remaining-count note below —
  // a contradiction/stale change must never be cleared silently (ADR-011).
  const resolveRun = (): void => {
    void review.confirmAll().then((done) => {
      if (done) onResolved?.();
    });
  };

  const pendingCount = changes.filter((c) => c.status === "pending").length;

  const isPanel = layout === "panel";
  const footerWrapClass = isPanel
    ? "sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-slate-200 flex items-center gap-3"
    : "";
  const footer = isChangeless ? (
    <div className={footerWrapClass}>
      <button
        type="button"
        onClick={resolveRun}
        data-testid="acknowledge-button"
        className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
      >
        Acknowledge
      </button>
      <span className="text-xs text-slate-400">
        No card changes to confirm — review the notes above.
      </span>
    </div>
  ) : (
    <div className={footerWrapClass}>
      <button
        type="button"
        onClick={resolveRun}
        disabled={!anyPending}
        data-testid="confirm-all-button"
        className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-40"
      >
        {isPanel ? "Accept all remaining" : "Confirm all"}
      </button>
      {isPanel && pendingCount < changes.length ? (
        <span className="text-xs text-amber-700" data-testid="remaining-note">
          {pendingCount} still need attention
        </span>
      ) : isPanel ? (
        <span className="text-xs text-slate-400">
          {changes.length} proposed change{changes.length === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="proposal-diff-review">
      {review.error !== null && (
        <div
          data-testid="review-error"
          className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700"
        >
          {review.error}
        </div>
      )}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
          Proposed changes
        </div>
        <div className="text-sm text-slate-700">
          {String(run.summary.headline ?? "Proposed changes from a source.")}
        </div>
        {sourceLabel !== null && (
          <div
            className="text-[11px] text-slate-500 mt-2"
            title={run.source_ref}
            data-source-ref={run.source_ref}
          >
            From {sourceLabel}
          </div>
        )}
      </div>

      {isPanel ? (
        <div className="space-y-2">
          {changes.map((change) => (
            <ChangeRow
              key={change.id}
              change={change}
              onAdjudicate={adjudicate}
              sourceTitles={sourceTitles}
            />
          ))}
        </div>
      ) : (
        changes.map((change) => (
          <ChangeCard
            key={change.id}
            change={change}
            onAdjudicate={adjudicate}
            sourceTitles={sourceTitles}
          />
        ))
      )}

      {unmappedNotes.length > 0 && (
        <div
          data-testid="unmapped-notes"
          className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2"
        >
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Notes not matched to a card
          </div>
          <p className="text-xs text-slate-500">
            The following content from your edits could not be matched to an
            existing card. No changes are proposed for these items — review them
            manually.
          </p>
          <ul className="space-y-1">
            {unmappedNotes.map((note, i) => (
              <li
                // Unmapped notes are read-only (no reorder/removal), so
                // index is a stable key within this fixed list.
                // biome-ignore lint: index key is fine for a static list
                key={`unmapped-${i}`}
                className="text-sm text-slate-700 pl-3 border-l-2 border-slate-300"
              >
                {note.quote}
                {note.rationale !== "" && (
                  <span className="block text-xs text-slate-500 mt-0.5">
                    {note.rationale}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {footer}
    </div>
  );
};

export default ProposalDiffReview;
